// packages
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Eye, Loader2, FlaskConical } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import { addHours } from 'date-fns'

// components
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CustomDataTable } from '@/components/table/custom-data-table'
import { PageHeader } from '@/components/common/page-header'
import { ExamAnalysisResultDialog } from '@/components/exam-analysis/result-dialog'

// libs
import { showConfirm } from '@/lib/alerts'
import { cn } from '@/lib/utils'

// hooks
import { useToast } from '@/hooks/use-toast'

// store
import { useExamAnalysisStore } from '@/store/exam-analysis'

// entities
import {
  ExamAnalysisProps,
  formatted_exam_analysis_category,
  formatted_exam_analysis_status,
  exam_analysis_status,
  formatContext
} from '@/entities/exam-analysis'

// utils
import { formatDate } from '@/utils/dates-util'

const loc = '@/pages/ExamAnalysis'

const STATUS_BADGE_CLASS: Record<exam_analysis_status, string> = {
  [exam_analysis_status.DRAFT]: 'bg-muted text-muted-foreground',
  [exam_analysis_status.PROCESSING]: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
  [exam_analysis_status.ACTIVE]: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
  [exam_analysis_status.FAILED]: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  [exam_analysis_status.DELETED]: 'bg-muted text-muted-foreground'
}

export default function ExamAnalysis() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const items = useExamAnalysisStore(s => s.items)
  const fetchAll = useExamAnalysisStore(s => s.fetchAll)
  const fetchByRef = useExamAnalysisStore(s => s.fetchByRef)
  const remove = useExamAnalysisStore(s => s.remove)

  const [previewRecord, setPreviewRecord] = useState<ExamAnalysisProps | null>(null)

  useEffect(() => {
    fetchAll()
  }, [])

  useEffect(() => {
    const hasProcessing = items.some(i => i.status === exam_analysis_status.PROCESSING)
    if (!hasProcessing) return
    const id = setInterval(() => {
      items.filter(i => i.status === exam_analysis_status.PROCESSING).forEach(i => fetchByRef(i.ref))
    }, 30000)
    return () => clearInterval(id)
  }, [items])

  const _handleDelete = async (record: ExamAnalysisProps) => {
    const fullLoc = `${loc}._handleDelete`
    try {
      const { isConfirmed } = await showConfirm(`Deseja remover a análise de "${record.patientName}"?`)
      if (!isConfirmed) return
      const ok = await remove(record.ref)
      if (ok) toast({ variant: 'success', title: 'Sucesso', description: 'Registro removido com sucesso.' })
      else toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível remover o registro.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível remover o registro.' })
      console.error(`Unhandled error at ${fullLoc}. Details: ${err}`)
    }
  }

  const columns: ColumnDef<ExamAnalysisProps>[] = [
    {
      accessorKey: 'patientName',
      header: 'Paciente',
      cell: ({ row }) => <span className="text-foreground font-semibold">{row.original.patientName}</span>
    },
    {
      accessorKey: 'category',
      header: 'Categoria',
      cell: ({ row }) => (
        <span className="rounded-md py-1 px-2 text-center text-xs font-semibold bg-primary/15 text-coentro">
          {formatted_exam_analysis_category[row.original.category]}
        </span>
      )
    },
    {
      id: 'context',
      header: 'Contexto',
      cell: ({ row }) => {
        const ctx = formatContext(row.original)
        if (ctx === '—') return <span className="text-xs text-muted-foreground italic">—</span>
        return <span className="rounded-md py-1 px-2 text-center text-[10px] font-semibold bg-accent/15 text-jenipapo">{ctx}</span>
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = row.original.status
        return (
          <span className={cn('inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider', STATUS_BADGE_CLASS[s])}>
            {s === exam_analysis_status.PROCESSING && <Loader2 className="h-3 w-3 animate-spin" />}
            {formatted_exam_analysis_status[s]}
          </span>
        )
      }
    },
    {
      id: 'aiSummary',
      header: 'Resumo IA',
      cell: ({ row }) => {
        const s = row.original.status
        if (s === exam_analysis_status.PROCESSING) return <span className="text-xs text-amber-700 italic">Aguardando análise da Nina...</span>
        if (s === exam_analysis_status.FAILED) return <span className="text-xs text-red-700 italic">Falha na geração</span>
        if (s !== exam_analysis_status.ACTIVE) return <span className="text-xs text-muted-foreground italic">Análise não gerada</span>
        return <p className="text-sm text-foreground/85 line-clamp-1 max-w-xs">{row.original.summary || 'Análise gerada.'}</p>
      }
    },
    {
      accessorKey: 'updatedAt',
      header: 'Atualizado',
      cell: ({ row }) => {
        const dt = new Date(row.original.updatedAt || row.original.createdAt)
        return <span className="text-foreground whitespace-nowrap">{formatDate(addHours(dt, 3))}</span>
      }
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const item = row.original
        const isActive = item.status === exam_analysis_status.ACTIVE
        const isProcessing = item.status === exam_analysis_status.PROCESSING
        return (
          <div className="flex justify-end gap-x-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => setPreviewRecord(item)} disabled={!isActive}>
                  <span className="sr-only">Visualizar</span>
                  <Eye className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                {isActive ? 'Visualizar análise' : 'Análise indisponível'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="default" size="sm" onClick={() => navigate(`/exam-analysis/${item.ref}`)} disabled={isProcessing}>
                  <span className="sr-only">Editar</span>
                  <Pencil className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                {isProcessing ? 'Aguarde a análise concluir' : 'Editar'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="default" size="sm" onClick={() => _handleDelete(item)} className="bg-red-700 text-white hover:bg-red-700/70 hover:brightness-125" disabled={isProcessing}>
                  <span className="sr-only">Deletar</span>
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-red-700 text-white text-xs">
                Deletar
              </TooltipContent>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  return (
    <>
      <div className="mx-0">
        <PageHeader title="Análise de Exames" description="Preencha os marcadores laboratoriais da paciente e gere uma análise clínica com apoio da Nina" />

        <div className="mt-5 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent p-5 shadow-soft">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 rounded-xl bg-primary/15 text-coentro p-3 ring-1 ring-primary/20">
              <FlaskConical className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground">Painel de Análise Laboratorial</h2>
              <p className="mt-1 text-sm sm:text-base text-muted-foreground leading-relaxed">
                Cadastre os resultados laboratoriais do paciente para comparar exames ao longo do tempo e receber interpretações, hipóteses diagnósticas e sugestões complementares.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl shadow-sm mt-5">
          <CustomDataTable columns={columns} data={items} filterKey={{ title: 'Paciente', value: 'patientName' }} amount={items.length}>
            <div className="w-full flex flex-row gap-y-2 space-between items-end smAndDown:flex-col smAndDown:gap-y-4 smAndDown:items-center">
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="default" onClick={() => navigate('/exam-analysis/new')} className="smAndDown:w-full">
                      <Plus className="size-5" />
                      Novo registro
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs mdAndUp:hidden">
                    Novo registro
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CustomDataTable>
        </div>
      </div>

      <ExamAnalysisResultDialog open={!!previewRecord} record={previewRecord} onClose={() => setPreviewRecord(null)} />
    </>
  )
}
